import { strToU8, zipSync } from "fflate";
import { localizeRuntimeText, t } from "./i18n";

const WORD_LIST_PDF_MODULE_URL = "/word-list-pdf.js";
type WordListPdfModule = typeof import("jspdf");

export type WordListOutputMode = "translation" | "hint" | "sentence" | "custom";

export type WordListEntry = {
    id: string;
    term: string;
    context: string;
    output: string;
};

export type WordListToolState = {
    accountWordListId: string | null;
    sourceText: string;
    title: string;
    mode: WordListOutputMode;
    autoAlphabetize: boolean;
    autoCapitalize: boolean;
    customTemplate: string;
    entries: WordListEntry[];
    loading: boolean;
    error: string;
};

type WordListGenerateResponse = {
    items: Array<{
        term: string;
        output: string;
    }>;
    model?: string;
};

type SplitPages = WordListEntry[][];
type SplitColumns = [WordListEntry[], WordListEntry[]];
type WordListDocumentOption = "autoAlphabetize" | "autoCapitalize";

type WordListToolView = {
    destroy: () => void;
    refreshLanguage: () => void;
};

type WordListToolOptions = {
    onSaveHistory?: (state: WordListToolState) => void;
};

type TranslationKey = Parameters<typeof t>[0];

const WORD_LIST_SOURCE_PLACEHOLDER = [
    "can",
    "cook <the person>",
    "possible <it was possible for him to see it>",
    "shell",
    "bottle",
    "light <not heavy>",
    "misunderstanding <the message BRB caused a misunderstanding>"
].join("\n");

const MODE_LABEL_KEYS: Record<WordListOutputMode, TranslationKey> = {
    translation: "wordListModeTranslation",
    hint: "wordListModeHint",
    sentence: "wordListModeSentence",
    custom: "wordListModeCustom"
};

const DOCUMENT_OPTION_LABEL_KEYS: Record<WordListDocumentOption, TranslationKey> = {
    autoAlphabetize: "wordListAutoAlphabetize",
    autoCapitalize: "wordListAutoCapitalize"
};

const DOCUMENT_OPTION_BUTTON_TEXT: Record<WordListDocumentOption, string> = {
    autoAlphabetize: "A-Z",
    autoCapitalize: "Aa"
};

const DEFAULT_CUSTOM_TEMPLATE =
    'For "{term}", write one short classroom-friendly clue. Use this context if helpful: "{context}".';

const DOCX_MIME = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const HWP_COMPATIBLE_MIME = "application/x-hwp";
const DOCUMENT_FONT_FAMILY = '"Cambria", "Malgun Gothic", "Noto Serif KR", serif';
const WORD_LIST_COLUMN_ENTRY_CAPACITY = 9;
const WORD_LIST_PAGE_ENTRY_CAPACITY = WORD_LIST_COLUMN_ENTRY_CAPACITY * 2;
const WORD_LIST_MIN_PREVIEW_PAGE_COUNT = 2;

const createEntryId = (): string => {
    const cryptoApi = globalThis.crypto;

    if (typeof cryptoApi?.randomUUID === "function") {
        return cryptoApi.randomUUID();
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createInitialWordListToolState = (): WordListToolState => ({
    accountWordListId: null,
    sourceText: "",
    title: "Vocabulary",
    mode: "translation",
    autoAlphabetize: false,
    autoCapitalize: false,
    customTemplate: DEFAULT_CUSTOM_TEMPLATE,
    entries: [],
    loading: false,
    error: ""
});

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

const escapeXml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");

const escapeAttribute = escapeHtml;

const normalizeWhitespace = (value: string): string => value.replace(/\s+/g, " ").trim();

const autoCapitalizeTerm = (term: string): string => {
    const normalized = normalizeWhitespace(term);

    return normalized
        .replace(/\bi\b/g, "I")
        .replace(/^([^A-Za-z]*)([a-z])/, (_match, prefix: string, letter: string) => `${prefix}${letter.toUpperCase()}`)
        .replace(/([.!?:]\s+)([a-z])/g, (_match, prefix: string, letter: string) => `${prefix}${letter.toUpperCase()}`);
};

const renderHighlightedSourceText = (value: string): string => {
    const normalizedValue = value.replace(/\r\n?/g, "\n");
    const contextPattern = /<[^<>\n]*>/g;
    let rendered = "";
    let lastIndex = 0;

    normalizedValue.replace(contextPattern, (match, offset: number) => {
        rendered += escapeHtml(normalizedValue.slice(lastIndex, offset));
        rendered += `<span class="word-list-source-context">${escapeHtml(match)}</span>`;
        lastIndex = offset + match.length;
        return match;
    });

    rendered += escapeHtml(normalizedValue.slice(lastIndex));

    return rendered || " ";
};

const getEntryKey = (entry: Pick<WordListEntry, "term" | "context">): string =>
    `${entry.term.trim().toLocaleLowerCase()}\u0000${entry.context.trim().toLocaleLowerCase()}`;

export const parseWordListSource = (
    sourceText: string,
    previousEntries: readonly WordListEntry[] = []
): WordListEntry[] => {
    const previousByKey = new Map<string, WordListEntry[]>();

    previousEntries.forEach((entry) => {
        const key = getEntryKey(entry);
        previousByKey.set(key, [...(previousByKey.get(key) ?? []), entry]);
    });

    return sourceText
        .replace(/\r\n?/g, "\n")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, 80)
        .map((line) => {
            const contextMatch = line.match(/^(.*?)\s*<([^<>]*)>\s*$/);
            const term = normalizeWhitespace(contextMatch ? contextMatch[1] ?? "" : line);
            const context = normalizeWhitespace(contextMatch ? contextMatch[2] ?? "" : "");
            const key = getEntryKey({ term, context });
            const previous = previousByKey.get(key)?.shift();

            return {
                id: previous?.id ?? createEntryId(),
                term,
                context,
                output: previous?.output ?? ""
            };
        })
        .filter((entry) => entry.term.length > 0);
};

const splitColumns = (entries: readonly WordListEntry[]): SplitColumns => [
    [...entries.slice(0, WORD_LIST_COLUMN_ENTRY_CAPACITY)],
    [...entries.slice(WORD_LIST_COLUMN_ENTRY_CAPACITY, WORD_LIST_PAGE_ENTRY_CAPACITY)]
];

const splitPages = (entries: readonly WordListEntry[]): SplitPages => {
    const pages: SplitPages = [];

    for (let index = 0; index < entries.length; index += WORD_LIST_PAGE_ENTRY_CAPACITY) {
        pages.push([...entries.slice(index, index + WORD_LIST_PAGE_ENTRY_CAPACITY)]);
    }

    if (pages.length === 0) {
        pages.push([]);
    }

    while (pages.length < WORD_LIST_MIN_PREVIEW_PAGE_COUNT) {
        pages.push([]);
    }

    return pages;
};

const formatFileDate = (): string => new Date().toISOString().slice(0, 10);

const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const getCleanTitle = (title: string): string => title.trim() || "Vocabulary";

const getExportEntries = (state: WordListToolState): WordListEntry[] => {
    const entries = parseWordListSource(state.sourceText, state.entries).map((entry) => ({
        ...entry,
        term: state.autoCapitalize ? autoCapitalizeTerm(entry.term) : entry.term
    }));

    if (!state.autoAlphabetize) {
        return entries;
    }

    return [...entries].sort((left, right) => {
        const termComparison = left.term.localeCompare(right.term, undefined, {
            numeric: true,
            sensitivity: "base"
        });

        return termComparison || left.context.localeCompare(right.context, undefined, { numeric: true, sensitivity: "base" });
    });
};

const hasGeneratedOutputs = (state: WordListToolState): boolean =>
    state.entries.some((entry) => entry.output.trim().length > 0);

const renderEntryOutput = (entry: WordListEntry, canEditOutputs: boolean, entryNumber: number): string => {
    if (canEditOutputs) {
        return `
            <label class="word-list-doc-output-shell">
                <span class="word-list-entry-number" aria-hidden="true">${entryNumber}</span>
                <textarea
                    class="word-list-doc-output-input"
                    data-word-list-output-id="${escapeAttribute(entry.id)}"
                    rows="1"
                    spellcheck="false"
                    aria-label="${escapeAttribute(`${t("wordListOutputPlaceholder")} ${entry.term}`)}"
                >${escapeHtml(entry.output)}</textarea>
            </label>
        `;
    }

    return entry.output.trim() ? `<span class="word-list-doc-output-text">${escapeHtml(entry.output.trim())}</span>` : "";
};

const renderPreviewPage = (
    entries: readonly WordListEntry[],
    title: string,
    pageIndex: number,
    pageOffset: number,
    canEditOutputs: boolean
): string => {
    const columns = splitColumns(entries);
    const density = Math.max(columns[0].length, columns[1].length, 1);

    return `
        <section
            class="word-list-doc-page"
            style="--word-list-page-density: ${density}"
            aria-label="${escapeAttribute(t("wordListPageLabel", { number: pageIndex + 1 }))}"
        >
            <div class="word-list-doc-title">${escapeHtml(title)}</div>
            <div class="word-list-doc-columns">
                ${columns
                    .map(
                        (column, columnIndex) => `
                            <div class="word-list-doc-column">
                                ${
                                    column.length > 0
                                        ? column
                                              .map(
                                                  (entry, rowIndex) => {
                                                      const entryNumber =
                                                          pageOffset + columnIndex * WORD_LIST_COLUMN_ENTRY_CAPACITY + rowIndex + 1;

                                                      return `
                                                    <div class="word-list-doc-entry">
                                                        <strong>${escapeHtml(entry.term)}</strong>
                                                        ${renderEntryOutput(entry, canEditOutputs, entryNumber)}
                                                    </div>
                                                `;
                                                  }
                                              )
                                              .join("")
                                        : `<div class="word-list-doc-entry is-empty"></div>`
                                }
                            </div>
                        `
                    )
                    .join("")}
            </div>
        </section>
    `;
};

const renderPreviewPages = (state: WordListToolState): string => {
    const entries = getExportEntries(state);
    const pages = splitPages(entries);
    const title = getCleanTitle(state.title);
    const canEditOutputs = hasGeneratedOutputs(state);
    let pageOffset = 0;

    return pages
        .map((pageEntries, index) => {
            const pageMarkup = renderPreviewPage(pageEntries, title, index, pageOffset, canEditOutputs);
            pageOffset += pageEntries.length;
            return pageMarkup;
        })
        .join("");
};

const renderModeButton = (mode: WordListOutputMode, activeMode: WordListOutputMode): string => `
    <button
        class="word-list-mode-button ${mode === activeMode ? "is-selected" : ""}"
        type="button"
        data-word-list-mode="${mode}"
        aria-pressed="${mode === activeMode ? "true" : "false"}"
    >
        ${escapeHtml(t(MODE_LABEL_KEYS[mode]))}
    </button>
`;

const renderDocumentOptionButton = (option: WordListDocumentOption, state: WordListToolState): string => {
    const isActive = state[option];
    const label = t(DOCUMENT_OPTION_LABEL_KEYS[option]);

    return `
        <button
            class="word-list-preview-option-button ${isActive ? "is-active" : ""}"
            type="button"
            data-word-list-option="${option}"
            aria-pressed="${isActive ? "true" : "false"}"
            aria-label="${escapeAttribute(label)}"
            title="${escapeAttribute(label)}"
        >
            ${escapeHtml(DOCUMENT_OPTION_BUTTON_TEXT[option])}
        </button>
    `;
};

const renderStatusText = (state: WordListToolState): string => {
    if (state.error) {
        return localizeRuntimeText(state.error);
    }

    if (state.loading) {
        return t("wordListGenerating");
    }

    const count = getExportEntries(state).length;

    return t("wordListReadyStatus", { count });
};

const renderToolMarkup = (state: WordListToolState): string => {
    const entries = getExportEntries(state);
    const hasEntries = entries.length > 0;
    const canExport = hasGeneratedOutputs(state);
    const controlPanelClass = ["word-list-control-panel", canExport ? "" : "is-active-section"].filter(Boolean).join(" ");
    const sourceShellClass = ["word-list-source-shell", canExport ? "" : "is-guided"].filter(Boolean).join(" ");
    const previewShellClass = ["word-list-preview-shell", canExport ? "is-active-section" : ""].filter(Boolean).join(" ");
    const generateButtonClass = [canExport ? "secondary-button" : "primary-button", "word-list-generate-button"].join(" ");

    return `
        <section class="word-list-tool">
            <div class="${controlPanelClass}">
                <div class="word-list-title-row">
                    <label class="word-list-title-field">
                        <span>${escapeHtml(t("wordListTitleLabel"))}</span>
                        <input
                            id="word-list-title-input"
                            type="text"
                            maxlength="80"
                            value="${escapeAttribute(state.title)}"
                        />
                    </label>
                    <span class="word-list-count-pill" data-word-list-count>${escapeHtml(
                        t("wordListReadyStatus", { count: entries.length })
                    )}</span>
                </div>
                <div class="word-list-mode-row" role="group" aria-label="${escapeAttribute(t("wordListModeLabel"))}">
                    ${(["translation", "hint", "sentence", "custom"] as const)
                        .map((mode) => renderModeButton(mode, state.mode))
                        .join("")}
                </div>
                ${
                    state.mode === "custom"
                        ? `
                            <textarea
                                id="word-list-custom-template"
                                class="word-list-custom-template"
                                rows="3"
                                spellcheck="false"
                                placeholder="${escapeAttribute(DEFAULT_CUSTOM_TEMPLATE)}"
                            >${escapeHtml(state.customTemplate)}</textarea>
                        `
                        : ""
                }
                <p class="word-list-context-note" id="word-list-context-note"><em>${escapeHtml(
                    t("wordListContextNote")
                )}</em></p>
                <div class="${sourceShellClass}">
                    <div class="word-list-source-highlight" aria-hidden="true">
                        <pre data-word-list-source-highlight-inner>${renderHighlightedSourceText(state.sourceText)}</pre>
                    </div>
                    <textarea
                        id="word-list-source-input"
                        class="word-list-source-input"
                        rows="11"
                        spellcheck="false"
                        aria-describedby="word-list-context-note"
                        placeholder="${escapeAttribute(WORD_LIST_SOURCE_PLACEHOLDER)}"
                    >${escapeHtml(state.sourceText)}</textarea>
                </div>
                <div class="word-list-action-row">
                    <button
                        class="${generateButtonClass}"
                        type="button"
                        data-word-list-generate
                        ${hasEntries && !state.loading ? "" : "disabled"}
                    >
                        ${escapeHtml(state.loading ? t("wordListGeneratingButton") : t("wordListGenerate"))}
                    </button>
                    <button
                        class="secondary-button word-list-export-button"
                        type="button"
                        data-word-list-download="docx"
                        ${canExport ? "" : "disabled"}
                    >
                        ${escapeHtml(t("wordListDownloadDocx"))}
                    </button>
                    <button
                        class="secondary-button word-list-export-button"
                        type="button"
                        data-word-list-download="pdf"
                        ${canExport ? "" : "disabled"}
                    >
                        ${escapeHtml(t("wordListDownloadPdf"))}
                    </button>
                    <button
                        class="secondary-button word-list-export-button"
                        type="button"
                        data-word-list-download="hwp"
                        ${canExport ? "" : "disabled"}
                    >
                        ${escapeHtml(t("wordListDownloadHwp"))}
                    </button>
                </div>
                <p class="word-list-status ${state.error ? "is-error" : ""}" data-word-list-status>${escapeHtml(
                    renderStatusText(state)
                )}</p>
            </div>
            <div class="word-list-workspace">
                <div class="${previewShellClass}">
                    <div class="word-list-preview-tools" role="group" aria-label="${escapeAttribute(
                        t("wordListDocumentOptionsLabel")
                    )}">
                        ${(["autoAlphabetize", "autoCapitalize"] as const)
                            .map((option) => renderDocumentOptionButton(option, state))
                            .join("")}
                    </div>
                    <div class="word-list-preview-pages" data-word-list-preview>
                        ${renderPreviewPages(state)}
                    </div>
                </div>
            </div>
        </section>
    `;
};

const xmlText = (value: string): string => {
    const escaped = escapeXml(value);
    const preserveSpace = /^\s|\s$|\s{2,}/.test(value);

    return `<w:t${preserveSpace ? ' xml:space="preserve"' : ""}>${escaped}</w:t>`;
};

const docxParagraph = (text: string, options: { bold?: boolean; size?: number; after?: number; indent?: number } = {}) => `
    <w:p>
        <w:pPr>
            <w:spacing w:after="${options.after ?? 40}" />
            ${options.indent ? `<w:ind w:left="${options.indent}" />` : ""}
        </w:pPr>
        <w:r>
            <w:rPr>
                <w:rFonts w:ascii="Cambria" w:eastAsia="Cambria" w:hAnsi="Cambria" />
                ${options.bold ? "<w:b />" : ""}
                <w:sz w:val="${options.size ?? 28}" />
                <w:szCs w:val="${options.size ?? 28}" />
            </w:rPr>
            ${xmlText(text)}
        </w:r>
    </w:p>
`;

const buildDocxTitleTable = (title: string): string => `
    <w:tbl>
        <w:tblPr>
            <w:tblW w:w="0" w:type="auto" />
            <w:jc w:val="center" />
            <w:tblLayout w:type="fixed" />
        </w:tblPr>
        <w:tblGrid><w:gridCol w:w="10512" /></w:tblGrid>
        <w:tr>
            <w:tc>
                <w:tcPr>
                    <w:tcW w:w="10512" w:type="dxa" />
                    <w:tcBorders>
                        <w:top w:val="single" w:sz="10" w:space="0" w:color="808080" />
                        <w:left w:val="single" w:sz="10" w:space="0" w:color="808080" />
                        <w:bottom w:val="single" w:sz="10" w:space="0" w:color="808080" />
                        <w:right w:val="single" w:sz="10" w:space="0" w:color="808080" />
                    </w:tcBorders>
                    <w:shd w:val="clear" w:color="auto" w:fill="F2F2F2" />
                    <w:tcMar>
                        <w:top w:w="120" w:type="dxa" />
                        <w:left w:w="120" w:type="dxa" />
                        <w:bottom w:w="120" w:type="dxa" />
                        <w:right w:w="120" w:type="dxa" />
                    </w:tcMar>
                    <w:vAlign w:val="center" />
                </w:tcPr>
                <w:p>
                    <w:pPr><w:jc w:val="center" /><w:spacing w:before="40" w:after="40" /></w:pPr>
                    <w:r><w:rPr><w:rFonts w:ascii="Cambria" w:eastAsia="Cambria" w:hAnsi="Cambria" /><w:b /><w:sz w:val="40" /></w:rPr>${xmlText(title)}</w:r>
                </w:p>
            </w:tc>
        </w:tr>
    </w:tbl>
`;

const buildDocxEntryCell = (entry: WordListEntry | null): string => {
    const output = entry?.output.trim() ?? "";

    return `
        <w:tc>
            <w:tcPr>
                <w:tcW w:w="5136" w:type="dxa" />
                <w:tcMar>
                    <w:top w:w="100" w:type="dxa" />
                    <w:left w:w="160" w:type="dxa" />
                    <w:bottom w:w="100" w:type="dxa" />
                    <w:right w:w="160" w:type="dxa" />
                </w:tcMar>
            </w:tcPr>
            ${entry ? docxParagraph(entry.term, { bold: true, size: 44, after: 0 }) : "<w:p />"}
            ${entry && output ? docxParagraph(output, { size: 28, after: 120, indent: 160 }) : "<w:p />"}
        </w:tc>
    `;
};

const buildDocxEntryTable = (entries: readonly WordListEntry[]): string => {
    const columns = splitColumns(entries);
    const rowCount = Math.max(columns[0].length, columns[1].length, 1);
    const rows = Array.from({ length: rowCount }, (_, index) => `
        <w:tr>
            ${buildDocxEntryCell(columns[0][index] ?? null)}
            ${buildDocxEntryCell(columns[1][index] ?? null)}
        </w:tr>
    `).join("");

    return `
        <w:tbl>
            <w:tblPr>
                <w:tblW w:w="0" w:type="auto" />
                <w:jc w:val="center" />
                <w:tblLayout w:type="fixed" />
                <w:tblCellMar>
                    <w:top w:w="0" w:type="dxa" />
                    <w:left w:w="0" w:type="dxa" />
                    <w:bottom w:w="0" w:type="dxa" />
                    <w:right w:w="0" w:type="dxa" />
                </w:tblCellMar>
            </w:tblPr>
            <w:tblGrid><w:gridCol w:w="5136" /><w:gridCol w:w="5136" /></w:tblGrid>
            ${rows}
        </w:tbl>
    `;
};

const buildDocxDocumentXml = (state: WordListToolState): string => {
    const pages = splitPages(getExportEntries(state));
    const title = getCleanTitle(state.title);
    const pageXml = pages
        .map(
            (pageEntries, index) => `
                ${buildDocxTitleTable(title)}
                <w:p><w:pPr><w:spacing w:after="120" /></w:pPr></w:p>
                ${buildDocxEntryTable(pageEntries)}
                ${index < pages.length - 1 ? '<w:p><w:r><w:br w:type="page" /></w:r></w:p>' : ""}
            `
        )
        .join("");

    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
        <w:document
            xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
            xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
        >
            <w:body>
                ${pageXml}
                <w:sectPr>
                    <w:pgSz w:w="12240" w:h="15840" />
                    <w:pgMar w:top="864" w:right="864" w:bottom="864" w:left="864" w:header="720" w:footer="720" w:gutter="0" />
                    <w:cols w:space="720" />
                    <w:docGrid w:linePitch="360" />
                </w:sectPr>
            </w:body>
        </w:document>
    `;
};

const buildDocxBlob = (state: WordListToolState): Blob => {
    const files: Record<string, Uint8Array> = {
        "[Content_Types].xml": strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
            <Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
                <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml" />
                <Default Extension="xml" ContentType="application/xml" />
                <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml" />
                <Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml" />
            </Types>
        `),
        "_rels/.rels": strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
            <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
                <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml" />
            </Relationships>
        `),
        "word/_rels/document.xml.rels": strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
            <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>
        `),
        "word/styles.xml": strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
            <w:styles xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
                <w:style w:type="paragraph" w:default="1" w:styleId="Normal">
                    <w:name w:val="Normal" />
                    <w:rPr>
                        <w:rFonts w:ascii="Cambria" w:hAnsi="Cambria" w:eastAsia="Cambria" />
                        <w:sz w:val="28" />
                    </w:rPr>
                </w:style>
            </w:styles>
        `),
        "word/document.xml": strToU8(buildDocxDocumentXml(state))
    };

    const zipped = zipSync(files, { level: 6 });
    const buffer = new ArrayBuffer(zipped.byteLength);
    new Uint8Array(buffer).set(zipped);

    return new Blob([buffer], { type: DOCX_MIME });
};

const buildHwpCompatibleHtml = (state: WordListToolState): string => {
    const pages = splitPages(getExportEntries(state));
    const title = getCleanTitle(state.title);

    return `<!doctype html>
        <html>
            <head>
                <meta charset="utf-8" />
                <title>${escapeHtml(title)}</title>
                <style>
                    @page { size: letter; margin: 0.6in; }
                    body { margin: 0; color: #111; font-family: ${DOCUMENT_FONT_FAMILY}; }
                    .page { min-height: 9.8in; page-break-after: always; }
                    .page:last-child { page-break-after: auto; }
                    .title { padding: 7px 0; border: 1px solid #808080; background: #f2f2f2; text-align: center; font-size: 20pt; font-weight: 700; }
                    .columns { display: grid; grid-template-columns: 1fr 1fr; gap: 0.32in; padding-top: 0.24in; }
                    .entry { break-inside: avoid; margin-bottom: 0.16in; }
                    .entry strong { display: block; font-size: 22pt; line-height: 1.02; }
                    .entry span { display: block; padding-left: 0.12in; font-size: 14pt; line-height: 1.2; }
                </style>
            </head>
            <body>
                ${pages
                    .map((pageEntries) => {
                        const columns = splitColumns(pageEntries);

                        return `
                            <section class="page">
                                <div class="title">${escapeHtml(title)}</div>
                                <div class="columns">
                                    ${columns
                                        .map(
                                            (column) => `
                                                <div>
                                                    ${column
                                                        .map(
                                                            (entry) => `
                                                                <div class="entry">
                                                                    <strong>${escapeHtml(entry.term)}</strong>
                                                                    <span>${escapeHtml(entry.output.trim())}</span>
                                                                </div>
                                                            `
                                                        )
                                                        .join("")}
                                                </div>
                                            `
                                        )
                                        .join("")}
                                </div>
                            </section>
                        `;
                    })
                    .join("")}
            </body>
        </html>
    `;
};

const wrapCanvasText = (
    context: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    maxLines: number
): string[] => {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let line = "";

    if (words.length === 0) {
        return [];
    }

    words.forEach((word) => {
        const candidate = line ? `${line} ${word}` : word;

        if (context.measureText(candidate).width <= maxWidth || !line) {
            line = candidate;
            return;
        }

        lines.push(line);
        line = word;
    });

    if (line) {
        lines.push(line);
    }

    if (lines.length <= maxLines) {
        return lines;
    }

    const clipped = lines.slice(0, maxLines);
    let lastLine = clipped[clipped.length - 1] ?? "";

    while (lastLine.length > 1 && context.measureText(`${lastLine}...`).width > maxWidth) {
        lastLine = lastLine.slice(0, -1);
    }

    clipped[clipped.length - 1] = `${lastLine}...`;
    return clipped;
};

const drawCanvasTextLines = (
    context: CanvasRenderingContext2D,
    lines: readonly string[],
    x: number,
    y: number,
    lineHeight: number
): number => {
    lines.forEach((line, index) => {
        context.fillText(line, x, y + index * lineHeight);
    });

    return y + lines.length * lineHeight;
};

const renderPdfPageCanvas = async (
    entries: readonly WordListEntry[],
    title: string,
    scale = 2
): Promise<HTMLCanvasElement> => {
    await document.fonts?.ready;

    const width = 612 * scale;
    const height = 792 * scale;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");

    if (!context) {
        throw new Error("Could not create the PDF canvas.");
    }

    const margin = 44 * scale;
    const titleHeight = 40 * scale;
    const columns = splitColumns(entries);
    const maxRows = Math.max(columns[0].length, columns[1].length, 1);
    const availableHeight = height - margin * 2 - titleHeight - 24 * scale;
    const rowHeight = Math.max(48 * scale, availableHeight / maxRows);
    const densityScale = Math.max(0.72, Math.min(1, rowHeight / (72 * scale)));
    const termSize = 25 * scale * densityScale;
    const outputSize = 15 * scale * densityScale;
    const columnGap = 24 * scale;
    const columnWidth = (width - margin * 2 - columnGap) / 2;
    const contentTop = margin + titleHeight + 26 * scale;

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.strokeStyle = "#808080";
    context.lineWidth = scale;
    context.fillStyle = "#f2f2f2";
    context.fillRect(margin, margin, width - margin * 2, titleHeight);
    context.strokeRect(margin, margin, width - margin * 2, titleHeight);
    context.fillStyle = "#111111";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.font = `700 ${20 * scale}px ${DOCUMENT_FONT_FAMILY}`;
    context.fillText(title, width / 2, margin + titleHeight / 2);
    context.textAlign = "left";
    context.textBaseline = "alphabetic";

    columns.forEach((column, columnIndex) => {
        const x = margin + columnIndex * (columnWidth + columnGap);

        column.forEach((entry, rowIndex) => {
            const y = contentTop + rowIndex * rowHeight;

            context.fillStyle = "#111111";
            context.font = `700 ${termSize}px ${DOCUMENT_FONT_FAMILY}`;
            const termLines = wrapCanvasText(context, entry.term, columnWidth, 2);
            const outputTop = drawCanvasTextLines(context, termLines, x, y, termSize * 1.02) + 9 * scale;
            context.fillStyle = "#333333";
            context.font = `400 ${outputSize}px ${DOCUMENT_FONT_FAMILY}`;
            const outputLines = wrapCanvasText(context, entry.output.trim(), columnWidth - 12 * scale, 2);
            drawCanvasTextLines(context, outputLines, x + 10 * scale, outputTop, outputSize * 1.28);
        });
    });

    return canvas;
};

const exportPdf = async (state: WordListToolState) => {
    // PDF rendering is an unlisted-tool-only cost, so keep its substantial dependency tree
    // out of the classroom landing/game bundle and load the local build only on export.
    const { jsPDF } = (await import(WORD_LIST_PDF_MODULE_URL)) as WordListPdfModule;
    const pdf = new jsPDF({ unit: "pt", format: "letter", orientation: "portrait" });
    const pages = splitPages(getExportEntries(state));
    const title = getCleanTitle(state.title);

    for (const [index, entries] of pages.entries()) {
        if (index > 0) {
            pdf.addPage("letter", "portrait");
        }

        const canvas = await renderPdfPageCanvas(entries, title);
        pdf.addImage(canvas.toDataURL("image/jpeg", 0.95), "JPEG", 0, 0, 612, 792);
    }

    pdf.save(`ziarcadia-word-list-${formatFileDate()}.pdf`);
};

class WordListTool {
    public constructor(
        private readonly root: HTMLElement,
        private readonly state: WordListToolState,
        private readonly options: WordListToolOptions = {}
    ) {
        this.state.entries = parseWordListSource(this.state.sourceText, this.state.entries);
        this.render();
        this.root.addEventListener("input", this.handleInput);
        this.root.addEventListener("click", this.handleClick);
        this.root.addEventListener("scroll", this.handleScroll, true);
    }

    public destroy() {
        this.root.removeEventListener("input", this.handleInput);
        this.root.removeEventListener("click", this.handleClick);
        this.root.removeEventListener("scroll", this.handleScroll, true);
    }

    public refreshLanguage() {
        this.render();
    }

    private render() {
        this.root.innerHTML = renderToolMarkup(this.state);
        this.syncSourceHighlight();
    }

    private syncDerivedSurfaces() {
        this.syncPreviewStatusAndButtons(true);
    }

    private syncPreviewStatusAndButtons(renderPreview: boolean) {
        const entries = getExportEntries(this.state);
        const canExport = hasGeneratedOutputs(this.state);
        if (renderPreview) {
            this.root.querySelector<HTMLElement>("[data-word-list-preview]")!.innerHTML = renderPreviewPages(this.state);
        }
        this.root.querySelector<HTMLElement>("[data-word-list-count]")!.textContent = t("wordListReadyStatus", {
            count: entries.length
        });
        const status = this.root.querySelector<HTMLElement>("[data-word-list-status]")!;
        status.textContent = renderStatusText(this.state);
        status.classList.toggle("is-error", Boolean(this.state.error));
        this.root.querySelector<HTMLElement>(".word-list-control-panel")?.classList.toggle("is-active-section", !canExport);
        this.root.querySelector<HTMLElement>(".word-list-source-shell")?.classList.toggle("is-guided", !canExport);
        this.root.querySelector<HTMLElement>(".word-list-preview-shell")?.classList.toggle("is-active-section", canExport);
        this.root.querySelectorAll<HTMLButtonElement>("[data-word-list-download]").forEach((button) => {
            button.disabled = !canExport;
        });
        const generateButton = this.root.querySelector<HTMLButtonElement>("[data-word-list-generate]");
        if (generateButton) {
            generateButton.disabled = entries.length === 0 || this.state.loading;
        }
    }

    private syncSourceHighlight(textarea = this.root.querySelector<HTMLTextAreaElement>("#word-list-source-input")) {
        if (!textarea) {
            return;
        }

        const highlightInner = textarea
            .closest(".word-list-source-shell")
            ?.querySelector<HTMLElement>("[data-word-list-source-highlight-inner]");

        if (!highlightInner) {
            return;
        }

        highlightInner.innerHTML = renderHighlightedSourceText(textarea.value);
        highlightInner.style.transform = `translate(${-textarea.scrollLeft}px, ${-textarea.scrollTop}px)`;
    }

    private handleInput = (event: Event) => {
        const target = event.target;

        if (target instanceof HTMLTextAreaElement && target.id === "word-list-source-input") {
            this.state.sourceText = target.value;
            this.state.entries = parseWordListSource(target.value, this.state.entries);
            this.state.error = "";
            this.syncSourceHighlight(target);
            this.syncDerivedSurfaces();
            return;
        }

        if (target instanceof HTMLInputElement && target.id === "word-list-title-input") {
            this.state.title = target.value;
            this.syncDerivedSurfaces();
            return;
        }

        if (target instanceof HTMLTextAreaElement && target.id === "word-list-custom-template") {
            this.state.customTemplate = target.value;
            return;
        }

        if (target instanceof HTMLTextAreaElement && target.hasAttribute("data-word-list-output-id")) {
            const id = target.getAttribute("data-word-list-output-id") ?? "";
            const entry = this.state.entries.find((candidate) => candidate.id === id);

            if (entry) {
                entry.output = target.value;
                this.syncPreviewStatusAndButtons(false);
            }
        }
    };

    private handleScroll = (event: Event) => {
        const target = event.target;

        if (target instanceof HTMLTextAreaElement && target.id === "word-list-source-input") {
            this.syncSourceHighlight(target);
        }
    };

    private handleClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        const modeButton = target?.closest<HTMLElement>("[data-word-list-mode]");
        const optionButton = target?.closest<HTMLElement>("[data-word-list-option]");
        const generateButton = target?.closest<HTMLElement>("[data-word-list-generate]");
        const downloadButton = target?.closest<HTMLElement>("[data-word-list-download]");

        if (modeButton) {
            const mode = modeButton.getAttribute("data-word-list-mode");

            if (mode === "translation" || mode === "hint" || mode === "sentence" || mode === "custom") {
                this.state.mode = mode;
                this.state.entries = parseWordListSource(this.state.sourceText).map((entry) => ({
                    ...entry,
                    output: ""
                }));
                this.state.error = "";
                this.render();
            }
            return;
        }

        if (optionButton) {
            const option = optionButton.getAttribute("data-word-list-option");

            if (option === "autoAlphabetize" || option === "autoCapitalize") {
                this.state[option] = !this.state[option];
                this.state.error = "";
                this.render();
            }
            return;
        }

        if (generateButton) {
            void this.generate();
            return;
        }

        if (downloadButton) {
            const format = downloadButton.getAttribute("data-word-list-download");
            void this.download(format);
        }
    };

    private async generate() {
        const entries = getExportEntries(this.state);

        if (entries.length === 0 || this.state.loading) {
            return;
        }

        this.state.loading = true;
        this.state.error = "";
        this.render();

        try {
            const response = await fetch("/api/word-list/generate", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    mode: this.state.mode,
                    customTemplate: this.state.customTemplate,
                    entries: entries.map((entry) => ({
                        term: entry.term,
                        context: entry.context
                    }))
                })
            });
            const data = (await response.json().catch(() => ({}))) as WordListGenerateResponse & { error?: string };

            if (!response.ok) {
                throw new Error(data.error ?? t("wordListGenerationFailed"));
            }

            const generatedByTerm = new Map<string, string[]>();
            data.items.forEach((item) => {
                const key = normalizeWhitespace(item.term).toLocaleLowerCase();
                generatedByTerm.set(key, [...(generatedByTerm.get(key) ?? []), item.output]);
            });

            this.state.entries = entries.map((entry, index) => {
                const key = entry.term.toLocaleLowerCase();
                const generated = generatedByTerm.get(key)?.shift() ?? data.items[index]?.output ?? "";

                return {
                    ...entry,
                    output: normalizeWhitespace(generated)
                };
            });
            this.options.onSaveHistory?.(this.state);
        } catch (error) {
            this.state.error = error instanceof Error ? error.message : t("wordListGenerationFailed");
        } finally {
            this.state.loading = false;
            this.render();
        }
    }

    private async download(format: string | null) {
        const entries = getExportEntries(this.state);

        if (entries.length === 0) {
            return;
        }

        try {
            if (format === "docx") {
                downloadBlob(buildDocxBlob(this.state), `ziarcadia-word-list-${formatFileDate()}.docx`);
                this.options.onSaveHistory?.(this.state);
                return;
            }

            if (format === "pdf") {
                await exportPdf(this.state);
                this.options.onSaveHistory?.(this.state);
                return;
            }

            if (format === "hwp") {
                downloadBlob(
                    new Blob([buildHwpCompatibleHtml(this.state)], { type: HWP_COMPATIBLE_MIME }),
                    `ziarcadia-word-list-${formatFileDate()}.hwp`
                );
                this.options.onSaveHistory?.(this.state);
            }
        } catch (error) {
            this.state.error = error instanceof Error ? error.message : t("wordListExportFailed");
            this.render();
        }
    }
}

export const createWordListTool = (
    root: HTMLElement,
    state: WordListToolState,
    options: WordListToolOptions = {}
): WordListToolView => new WordListTool(root, state, options);
